import React from 'react';
import {
  Avatar,
  Divider,
  Popover,
  Stack,
  Text,
  UnstyledButton,
  useComputedColorScheme,
  useMantineColorScheme,
} from '@mantine/core';
import { IconCheck, IconDoorExit } from '@tabler/icons-react';
import { getDisplayUserName } from '../../utils/userDisplay';
import {
  getSidebarAutoCollapse,
  setSidebarAutoCollapse,
} from '../../navigation/sidebarPreference';

function getInitials(displayName: string): string {
  return (
    displayName
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0]?.toUpperCase())
      .join('') || '?'
  );
}

function handleExit() {
  // Browsers only allow this for windows opened by script; still the intended UX for now.
  window.close();
}

function ProfileMenuToggle({
  label,
  description,
  checked,
  onToggle,
}: {
  label: string;
  description: string;
  checked: boolean;
  onToggle: () => void;
}) {
  return (
    <UnstyledButton
      className="header-user-menu__setting"
      role="menuitemcheckbox"
      aria-checked={checked}
      onClick={onToggle}
    >
      <span className="header-user-menu__setting-copy">
        <Text size="sm" fw={600}>
          {label}
        </Text>
        <Text size="xs" c="dimmed">
          {description}
        </Text>
      </span>
      {checked ? <IconCheck size={16} stroke={2} aria-hidden /> : null}
    </UnstyledButton>
  );
}

/** User avatar in the app header with a small account popover. */
export function HeaderUserAvatar() {
  const [opened, setOpened] = React.useState(false);
  const [sidebarAutoCollapse, setSidebarAutoCollapseState] = React.useState(getSidebarAutoCollapse);
  const { setColorScheme } = useMantineColorScheme();
  const computed = useComputedColorScheme('light', { getInitialValueInEffect: true });
  const isDark = computed === 'dark';
  const displayName = getDisplayUserName() ?? 'Account';
  const initials = getInitials(displayName);

  return (
    <Popover
      opened={opened}
      onChange={setOpened}
      position="bottom-end"
      withArrow
      shadow="md"
      width={260}
    >
      <Popover.Target>
        <UnstyledButton
          onClick={() => setOpened((o) => !o)}
          aria-label={`${displayName} account menu`}
          aria-expanded={opened}
          aria-haspopup="menu"
          style={{
            display: 'inline-flex',
            borderRadius: '50%',
            flexShrink: 0,
          }}
        >
          <Avatar
            radius="xl"
            size={32}
            className="header-user-avatar"
            styles={{
              placeholder: {
                fontSize: 13,
                fontWeight: 600,
                color: 'var(--mantine-color-white)',
              },
            }}
          >
            {initials}
          </Avatar>
        </UnstyledButton>
      </Popover.Target>
      <Popover.Dropdown p="xs">
        <Stack gap={4}>
          <Text size="xs" c="dimmed" px={8} pt={4} pb={2} lineClamp={1} title={displayName}>
            {displayName}
          </Text>
          <ProfileMenuToggle
            label="Auto-collapse sidebar"
            description="Keep the left sidebar collapsed; hover to expand it temporarily."
            checked={sidebarAutoCollapse}
            onToggle={() => {
              const enabled = !sidebarAutoCollapse;
              setSidebarAutoCollapse(enabled);
              setSidebarAutoCollapseState(enabled);
            }}
          />
          <ProfileMenuToggle
            label="Dark mode"
            description="Use a dark color theme for the app."
            checked={isDark}
            onToggle={() => setColorScheme(isDark ? 'light' : 'dark')}
          />
          <Divider my={4} />
          <UnstyledButton
            className="header-user-menu__item"
            onClick={() => {
              setOpened(false);
              handleExit();
            }}
          >
            <IconDoorExit size={16} stroke={1.5} />
            <span>Exit</span>
          </UnstyledButton>
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
}
